import React, { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { openAgent } from "../utils/openAgent";

const NAV_ITEMS = [
  { label: "Why Simpel", to: "/" },
  { label: "Who We Are", to: "/about" },
  { label: "What We Do", to: "/what-we-do" },
  { label: "How We Do", to: "/transform" },
  {
    label: "AI Factory",
    hasDropdown: true,
    dropdownItems: [
      {
        title: "Cognitive AI",
        desc: "Autonomous agents, enterprise intelligence & cognitive workflows",
        to: "/ai-opportunities",
      },
      {
        title: "Physical AI",
        desc: "Robotics, AMRs, factory automation & physical systems",
        to: "/cognitive",
      },
    ],
  },
  { label: "Insights", to: "/labs" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const closeTimeout = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState("Why Simpel");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleOpenDropdown = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setDropdownOpen(true);
  };

  const handleCloseDropdown = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    closeTimeout.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 180);
  };

  useEffect(() => {
    const path = location.pathname;
    if (path === "/about") {
      setActiveItem("Who We Are");
    } else if (
      path.startsWith("/what-we-do") ||
      path.startsWith("/services") ||
      path.startsWith("/product") ||
      path.startsWith("/stack") ||
      path.startsWith("/sales-automation") ||
      path.startsWith("/manufacturing-automation")
    ) {
      setActiveItem("What We Do");
    } else if (path === "/transform") {
      setActiveItem("How We Do");
    } else if (
      path === "/ai-opportunities" ||
      path === "/cognitive" ||
      path.startsWith("/case-studies") ||
      path.startsWith("/skipper-limited")
    ) {
      setActiveItem("AI Factory");
    } else if (path === "/labs" || path === "/insights") {
      setActiveItem("Insights");
    } else if (path === "/") {
      setActiveItem("Why Simpel");
    } else {
      setActiveItem("");
    }
  }, [location.pathname]);

  const handleNavClick = (item) => {
    setOpen(false);
    setDropdownOpen(false);
    setActiveItem(item.label);

    if (item.to === "/") {
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    if (item.to) {
      navigate(item.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
  };

  const handleDropdownItemClick = (sub) => {
    setDropdownOpen(false);
    setOpen(false);
    if (sub.to) {
      navigate(sub.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#010710]/95 backdrop-blur-md border-b border-white/5 shadow-lg shadow-black/40"
          : "bg-[#010710] border-b border-white/[0.04]"
      }`}
    >
      <div className="mx-auto flex h-[76px] sm:h-[80px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
        {/* Brand Logo */}
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
        >
          <img
            src="/images/simpel-icon-mark.png"
            alt="Simpel"
            className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="flex flex-col justify-center">
            <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
              Simpel
            </span>
            <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
              AUTOMATION SIMPLIFIED
            </span>
          </div>
        </Link>

        {/* Desktop Navigation Links + Let's Talk CTA */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          <nav className="flex items-center gap-5 xl:gap-7">
            {NAV_ITEMS.map((item) => {
              const active = activeItem === item.label;

              if (item.hasDropdown) {
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={handleOpenDropdown}
                    onMouseLeave={handleCloseDropdown}
                  >
                    <button
                      onClick={() => setDropdownOpen((prev) => !prev)}
                      className={`relative flex items-center gap-1.5 py-1.5 text-[14px] xl:text-[14.5px] font-medium transition-colors duration-300 cursor-pointer ${
                        active ? "text-[#00D2EE]" : "text-white/85 hover:text-white"
                      }`}
                    >
                      <span className="relative inline-block pb-1.5 whitespace-nowrap">
                        {item.label}
                        {active && (
                          <motion.span
                            layoutId="activeNavLine"
                            className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-full bg-gradient-to-r from-[#00D2EE] to-[#2F6DF6]"
                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                          />
                        )}
                      </span>
                      <ChevronDown
                        className={`h-3.5 w-3.5 text-white/70 transition-transform duration-200 ${
                          dropdownOpen ? "rotate-180 text-[#00D2EE]" : ""
                        }`}
                      />
                    </button>

                    {/* Dropdown Menu for AI Factory */}
                    <AnimatePresence>
                      {dropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 6, scale: 0.96 }}
                          transition={{ duration: 0.16, ease: "easeOut" }}
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-2.5 z-50 w-[300px]"
                        >
                          <div
                            className="rounded-[18px] p-2 shadow-2xl backdrop-blur-xl"
                            style={{
                              backgroundColor: "rgba(1, 7, 16, 0.98)",
                              border: "1px solid rgba(255, 255, 255, 0.12)",
                              boxShadow: "0 20px 45px rgba(0, 0, 0, 0.9)",
                            }}
                          >
                            {item.dropdownItems.map((sub) => (
                              <button
                                key={sub.title}
                                onClick={() => handleDropdownItemClick(sub)}
                                className="w-full flex flex-col items-start p-3 rounded-[12px] text-left transition-all duration-200 hover:bg-white/[0.08] group cursor-pointer"
                              >
                                <div className="flex items-center justify-between w-full">
                                  <span className="text-[14.5px] font-semibold text-white group-hover:text-[#00D2EE] transition-colors">
                                    {sub.title}
                                  </span>
                                  <ArrowRight className="h-3.5 w-3.5 text-white/40 group-hover:text-[#00D2EE] group-hover:translate-x-0.5 transition-all" />
                                </div>
                                <span className="mt-1 text-[11.5px] leading-[16px] text-[#94A3B8] group-hover:text-white/80 transition-colors">
                                  {sub.desc}
                                </span>
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className={`relative py-1.5 text-[14px] xl:text-[14.5px] font-medium transition-colors duration-300 cursor-pointer ${
                    active ? "text-[#00D2EE]" : "text-white/85 hover:text-white"
                  }`}
                >
                  <span className="relative inline-block pb-1.5 whitespace-nowrap">
                    {item.label}
                    {active && (
                      <motion.span
                        layoutId="activeNavLine"
                        className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-full bg-gradient-to-r from-[#00D2EE] to-[#2F6DF6]"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </nav>

          <button
            onClick={openAgent}
            className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#00D2EE] px-5 py-2.5 text-[14px] font-bold text-[#010710] shadow-[0_0_20px_rgba(0,210,238,0.35)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_28px_rgba(0,210,238,0.55)] hover:brightness-105 active:scale-95 cursor-pointer shrink-0"
          >
            <span>Let's Talk</span>
            <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>

        {/* Mobile Hamburger Button */}
        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg text-white lg:hidden hover:bg-white/10 transition-colors"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#010710]/98 px-6 pb-6 pt-3 shadow-2xl backdrop-blur-xl max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col space-y-1">
            {NAV_ITEMS.map((item) => {
              const active = activeItem === item.label;

              if (item.hasDropdown) {
                return (
                  <div key={item.label} className="flex flex-col border-b border-white/5 pb-2">
                    <span className="py-2.5 text-[15px] font-semibold text-white/70">
                      {item.label}
                    </span>
                    <div className="pl-3 flex flex-col gap-1 border-l border-white/10 ml-2">
                      {item.dropdownItems.map((sub) => (
                        <button
                          key={sub.title}
                          onClick={() => handleDropdownItemClick(sub)}
                          className="flex flex-col items-start py-2 text-left"
                        >
                          <span className="text-[14.5px] font-semibold text-white hover:text-[#00D2EE]">
                            {sub.title}
                          </span>
                          <span className="text-[12px] text-[#94A3B8] leading-tight mt-0.5">
                            {sub.desc}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className={`flex items-center justify-between py-3 text-left text-[15px] transition-colors duration-300 ${
                    active ? "font-semibold text-[#00D2EE]" : "text-white/85"
                  }`}
                >
                  <span>{item.label}</span>
                  {active && (
                    <span className="h-1.5 w-1.5 rounded-full bg-[#00D2EE]" />
                  )}
                </button>
              );
            })}

            {/* Mobile Let's Talk CTA */}
            <div className="pt-4">
              <button
                onClick={() => {
                  setOpen(false);
                  openAgent();
                }}
                className="w-full flex items-center justify-center gap-2 rounded-[12px] bg-[#00D2EE] px-5 py-3 text-[14.5px] font-bold text-[#010710] shadow-[0_0_20px_rgba(0,210,238,0.4)]"
              >
                <span>Let's Talk</span>
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
